import { jsx as _jsx } from "react/jsx-runtime";
import { ApolloClient, ApolloProvider, createHttpLink, from, InMemoryCache, } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { getApiUrl } from 'utils/config';
import { HttpStatusCodes } from 'utils/http-status-codes';
import { getIframeToken, isInIframe } from 'utils/iframe';
import { getLoginUrl } from 'utils/links';
import { log, Severity } from 'utils/logger';
import { getLocationRoute } from 'utils/routing';
import { isValidationError } from './error';
const getApolloClientConfig = ({ navigate, }) => {
    const link = createHttpLink({
        credentials: 'include',
        uri: `${getApiUrl()}/api/query`,
    });
    // authLink: In iFrame context, adds Bearer token to all GraphQL requests.
    // In normal context, this is a no-op (cookies handle auth via credentials: 'include').
    const authLink = setContext((_, { headers }) => {
        if (isInIframe()) {
            const token = getIframeToken();
            if (token) {
                return {
                    headers: {
                        ...headers,
                        Authorization: `Bearer ${token}`,
                    },
                };
            }
        }
        return { headers: headers };
    });
    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
            if (graphQLErrors[0]?.extensions?.code ===
                'auth.session.reauthenticationRequired') {
                navigate(getLoginUrl({
                    refresh: true,
                    returnPath: getLocationRoute(),
                }));
                return;
            }
            log('Apollo GraphQL error', {
                dump: graphQLErrors,
                severity: isValidationError(graphQLErrors)
                    ? Severity.WARN
                    : Severity.ERROR,
            });
        }
        if (networkError &&
            'statusCode' in networkError &&
            networkError.statusCode === HttpStatusCodes.UNAUTHORIZED) {
            navigate(getLoginUrl({
                returnPath: getLocationRoute(),
            }));
            return;
        }
        if (networkError) {
            log('Apollo network error', {
                dump: networkError,
                severity: Severity.ERROR,
            });
        }
    });
    return new ApolloClient({
        cache: new InMemoryCache(),
        link: from([errorLink, authLink, link]),
        defaultOptions: {
            query: { fetchPolicy: 'network-only' },
            watchQuery: { fetchPolicy: 'cache-and-network' },
        },
    });
};
export const Api = () => {
    const navigate = useNavigate();
    const client = React.useMemo(() => {
        return getApolloClientConfig({ navigate });
    }, [navigate]);
    return (_jsx(ApolloProvider, { client: client, children: _jsx(Outlet, {}) }));
};
